import * as THREE from 'three';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import TWEEN from '@tweenjs/tween.js';
import UIEventBus from '../UI/EventBus';
import BezierEasing from 'bezier-easing';
import { MonitorKeyframe, IdleKeyframe, LoadingKeyframe, DeskKeyframe, OrbitControlsStart, } from './CameraKeyframes';
import { ORBIT_VIEW_CONFIG, MOBILE_PORTRAIT_DESK_LOOK_AT_SCREEN, MOBILE_PORTRAIT_DESK_CAMERA_ROTATION_Y, MOBILE_PORTRAIT_DESK_CAMERA_ROTATION_Z, } from '../World/deskSceneConfig';
export var CameraKey;
(function (CameraKey) {
    CameraKey["IDLE"] = "idle";
    CameraKey["MONITOR"] = "monitor";
    CameraKey["LOADING"] = "loading";
    CameraKey["DESK"] = "desk";
    CameraKey["ORBIT_CONTROLS_START"] = "orbitControlsStart";
})(CameraKey || (CameraKey = {}));
export default class Camera extends EventEmitter {
    constructor() {
        super();
        this.application = new Application();
        this.sizes = this.application.sizes;
        this.scene = this.application.scene;
        this.renderer = this.application.renderer;
        this.resources = this.application.resources;
        this.time = this.application.time;
        this.position = new THREE.Vector3(0, 0, 0);
        this.focalPoint = new THREE.Vector3(0, 0, 0);
        this.freeCam = false;
        this.lastTransitionByTouch = 0;
        this.keyframes = {
            idle: new IdleKeyframe(),
            monitor: new MonitorKeyframe(),
            loading: new LoadingKeyframe(),
            desk: new DeskKeyframe(),
            orbitControlsStart: new OrbitControlsStart(),
        };
        const handleCameraTransition = (event, isTouch) => {
            var _a;
            const target = event.target;
            if (target.id === 'prevent-click')
                return;
            if (target.id === 'computer-screen' || ((_a = target.closest) === null || _a === void 0 ? void 0 : _a.call(target, '#computer-screen')))
                return;
            if (this.currentKeyframe === CameraKey.IDLE ||
                this.targetKeyframe === CameraKey.IDLE) {
                if (isTouch)
                    this.lastTransitionByTouch = Date.now();
                this.transition(CameraKey.DESK);
            }
            else if (this.currentKeyframe === CameraKey.DESK ||
                this.targetKeyframe === CameraKey.DESK) {
                if (isTouch)
                    this.lastTransitionByTouch = Date.now();
                this.transition(CameraKey.IDLE);
            }
        };
        document.addEventListener('mousedown', (event) => {
            event.preventDefault();
            if (event.target.id === 'prevent-click')
                return;
            if (Date.now() - this.lastTransitionByTouch < 280)
                return;
            handleCameraTransition(event, false);
        });
        document.addEventListener('touchstart', (event) => {
            var _a;
            const target = event.target;
            if (target.id === 'computer-screen' || ((_a = target.closest) === null || _a === void 0 ? void 0 : _a.call(target, '#computer-screen')))
                return;
            handleCameraTransition(event, true);
        }, { passive: true });
        this.setPostLoadTransition();
        this.setInstance();
        this.setMonitorListeners();
        this.setFreeCamListeners();
    }
    transition(key, duration = 600, easing, callback) {
        if (this.currentKeyframe === key)
            return;
        if (this.targetKeyframe)
            TWEEN.removeAll();
        this.currentKeyframe = undefined;
        this.targetKeyframe = key;
        const keyframe = this.keyframes[key];
        const posTween = new TWEEN.Tween(this.position)
            .to(keyframe.position, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut)
            .onComplete(() => {
            this.currentKeyframe = key;
            this.targetKeyframe = undefined;
            if (callback)
                callback();
        });
        const focTween = new TWEEN.Tween(this.focalPoint)
            .to(keyframe.focalPoint, duration)
            .easing(easing || TWEEN.Easing.Quintic.InOut);
        posTween.start();
        focTween.start();
    }
    setInstance() {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 10, 900000);
        this.currentKeyframe = CameraKey.LOADING;
        this.scene.add(this.instance);
    }
    setMonitorListeners() {
        this.on('enterMonitor', () => {
            this.transition(CameraKey.MONITOR, 1000, BezierEasing(0.13, 0.99, 0, 1));
            UIEventBus.dispatch('enterMonitor', {});
        });
        this.on('leftMonitor', () => {
            this.transition(CameraKey.DESK);
            UIEventBus.dispatch('leftMonitor', {});
        });
    }
    setFreeCamListeners() {
        UIEventBus.on('freeCamToggle', (toggle) => {
            // if (toggle === this.freeCam) return;
            if (toggle) {
                this.transition(CameraKey.ORBIT_CONTROLS_START, 750, BezierEasing(0.13, 0.99, 0, 1), () => {
                    this.instance.position.copy(this.keyframes.orbitControlsStart.position);
                    this.orbitControls.update();
                    this.freeCam = true;
                });
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'auto';
            }
            else {
                this.freeCam = false;
                this.transition(CameraKey.IDLE, 1800, TWEEN.Easing.Exponential.Out);
                // @ts-ignore
                document.getElementById('webgl').style.pointerEvents = 'none';
            }
        });
    }
    setPostLoadTransition() {
        UIEventBus.on('loadingScreenDone', () => {
            this.transition(CameraKey.IDLE, 1200, TWEEN.Easing.Exponential.Out);
        });
    }
    resize() {
        const w = this.sizes.width;
        const h = Math.max(this.sizes.height, 1);
        this.instance.aspect = w / h;
        this.instance.updateProjectionMatrix();
    }
    createControls() {
        this.renderer = this.application.renderer;
        this.orbitControls = new OrbitControls(this.instance, this.renderer.instance.domElement);
        const { x, y, z } = this.keyframes.orbitControlsStart.focalPoint;
        this.orbitControls.target.set(x, y, z);
        this.orbitControls.enablePan = false;
        this.orbitControls.enableDamping = true;
        this.orbitControls.object.position.copy(this.keyframes.orbitControlsStart.position);
        this.orbitControls.dampingFactor = 0.05;
        this.orbitControls.maxPolarAngle = Math.PI / 2;
        this.orbitControls.minDistance = ORBIT_VIEW_CONFIG.minDistance;
        this.orbitControls.maxDistance = ORBIT_VIEW_CONFIG.maxDistance;
        this.orbitControls.update();
    }
    update() {
        TWEEN.update();
        if (this.freeCam && this.orbitControls) {
            this.position.copy(this.orbitControls.object.position);
            this.focalPoint.copy(this.orbitControls.target);
            clampVector3ToFinite(this.position);
            clampVector3ToFinite(this.focalPoint);
            this.orbitControls.update();
            return;
        }
        for (const key in this.keyframes) {
            const _key = key;
            this.keyframes[_key].update();
        }
        if (this.currentKeyframe) {
            const keyframe = this.keyframes[this.currentKeyframe];
            this.position.copy(keyframe.position);
            this.focalPoint.copy(keyframe.focalPoint);
        }
        clampVector3ToFinite(this.position);
        clampVector3ToFinite(this.focalPoint);
        this.instance.position.set(this.position.x, this.position.y, this.position.z);
        // Avoid lookAt when position ≈ focalPoint (produces NaN and breaks AudioListener)
        const distSq = this.position.distanceToSquared(this.focalPoint);
        if (distSq > 1e-6) {
            this.instance.lookAt(this.focalPoint.x, this.focalPoint.y, this.focalPoint.z);
            // Mobile vertical only: apply rotation via quaternion (avoids euler gimbal lock → NaN → AudioListener error)
            if (this.currentKeyframe === CameraKey.DESK &&
                this.sizes.isMobilePortrait() &&
                !MOBILE_PORTRAIT_DESK_LOOK_AT_SCREEN) {
                const rotY = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, MOBILE_PORTRAIT_DESK_CAMERA_ROTATION_Y, 0, 'YXZ'));
                const rotZ = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, MOBILE_PORTRAIT_DESK_CAMERA_ROTATION_Z, 'YXZ'));
                this.instance.quaternion.premultiply(rotZ).premultiply(rotY);
            }
        }
        // Prevent AudioListener from receiving non-finite values (avoids linearRampToValueAtTime error)
        this.ensureFiniteForListener();
    }
    /** Call before render so AudioListener never receives non-finite values. Public for Renderer to call as last safeguard. */
    ensureFiniteForListener() {
        clampCameraToFinite(this.instance);
    }
}
function clampVector3ToFinite(v) {
    if (!Number.isFinite(v.x))
        v.x = 0;
    if (!Number.isFinite(v.y))
        v.y = 0;
    if (!Number.isFinite(v.z))
        v.z = 0;
}
function clampCameraToFinite(camera) {
    const p = camera.position;
    if (!Number.isFinite(p.x))
        p.x = 0;
    if (!Number.isFinite(p.y))
        p.y = 0;
    if (!Number.isFinite(p.z))
        p.z = 0;
    const q = camera.quaternion;
    if (!Number.isFinite(q.x) || !Number.isFinite(q.y) || !Number.isFinite(q.z) || !Number.isFinite(q.w)) {
        camera.quaternion.set(0, 0, 0, 1);
    }
    else {
        const lenSq = q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w;
        if (lenSq < 1e-12) {
            camera.quaternion.set(0, 0, 0, 1);
        }
        else {
            camera.quaternion.normalize();
        }
    }
    // Sync euler from quaternion so object is consistent (rotation can be read later)
    camera.rotation.setFromQuaternion(camera.quaternion, camera.rotation.order);
    const r = camera.rotation;
    if (!Number.isFinite(r.x) || !Number.isFinite(r.y) || !Number.isFinite(r.z)) {
        camera.rotation.set(0, 0, 0);
        camera.quaternion.set(0, 0, 0, 1);
    }
}
